import { Calculator, Close } from '@carbon/react/icons';
import { Button, Loading, DefinitionTooltip } from '@carbon/react';
import { useAtom, useSetAtom } from 'jotai';
import { subscribe } from 'lib/pusher';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TitleCalculationResponse } from 'types/api-responses';
import { CalculateFormProgress } from 'types/calculate-form-progress';
import { FeatureFlagWindow } from 'types/window';
import { getCurrentDateString } from 'utils/dates';

import style from './recalculate-workspace.module.scss';
import {
  newTitleCalculationAtom,
  cancelTitleCalculationAtom,
  titleCalculationPusherErrorMessageAtom,
  titleCalculationActivityAtom,
} from 'atoms/title-calculation-atoms';

declare let window: FeatureFlagWindow;

const RecalculateWorkspace = ({
  calculationsHref,
  onSuccess,
  onTitleCalculationError,
  startingInterestDate,
  runningCalculationChannel,
  runningCalculationHref,
  runningCalculationCancelHref,
}: {
  calculationsHref: string;
  onSuccess?: (href: string) => void;
  onTitleCalculationError: (isTitleCalculationError: boolean) => void;
  startingInterestDate: string | null | undefined;
  runningCalculationChannel: string | null;
  runningCalculationHref?: string;
  runningCalculationCancelHref?: string;
}) => {
  const setTitleCalculationPusherErrorMessage = useSetAtom(
    titleCalculationPusherErrorMessageAtom
  );
  const setCalculationActivity = useSetAtom(titleCalculationActivityAtom);
  const [calculation, runCalculation] = useAtom(newTitleCalculationAtom);
  const [cancelCalculationState, cancelCalculation] = useAtom(
    cancelTitleCalculationAtom
  );
  const [isTitlePusherNotificationActive, setIsTitlePusherNotificationActive] =
    useState(false);
  const [newCalculation, setNewCalculation] = useState<
    TitleCalculationResponse | undefined
  >(undefined);

  const channel =
    runningCalculationChannel || newCalculation?.notifications.channel;

  const event = newCalculation?.notifications.events[0].name || 'change';

  const calculationHref = runningCalculationHref || newCalculation?.href;

  // Ref mirror of onSuccess — lets the pusher subscription call the latest
  // callback without adding an unstable prop to the effect's dep list.
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (!channel || !calculationHref) {
      return () => void 0;
    }

    if (runningCalculationChannel) {
      setIsTitlePusherNotificationActive(true);
      setCalculationActivity((prev) => {
        if (prev.isCalculating) return prev;
        return {
          isCalculating: true,
          startedAt: new Date().toISOString(),
          completedAt: null,
          progress: 0,
        };
      });
    }
    const subscription = subscribe(channel);
    subscription.bind(event, (data: CalculateFormProgress) => {
      if ('calcStatus' in data && 'completionPercentage' in data) {
        setCalculationActivity((prev) => ({
          ...prev,
          progress:
            typeof data.completionPercentage === 'number'
              ? data.completionPercentage
              : prev.progress,
        }));
        if (data.calcStatus === 'error') {
          setIsTitlePusherNotificationActive(false);
          setTitleCalculationPusherErrorMessage(data.errorMessage);
          setCalculationActivity((prev) => ({
            ...prev,
            isCalculating: false,
            completedAt: new Date().toISOString(),
          }));
          subscription.unsubscribe();
        }
        if (data.calcStatus === 'complete' || data.calcStatus === 'cancelled') {
          setIsTitlePusherNotificationActive(false);
          setCalculationActivity((prev) => ({
            ...prev,
            isCalculating: false,
            completedAt: new Date().toISOString(),
          }));
          onSuccessRef.current?.(calculationHref);
          subscription.unsubscribe();
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [
    calculationHref,
    channel,
    event,
    runningCalculationChannel,
    setCalculationActivity,
    setTitleCalculationPusherErrorMessage,
  ]);

  const cancelHref =
    runningCalculationCancelHref || newCalculation?.resources.cancel.href;

  const handleCancelCalculation = useCallback(() => {
    if (cancelCalculationState.loading || !calculationHref || !cancelHref)
      return;

    cancelCalculation({
      type: 'PATCH',
      url: cancelHref,
      data: null,
      onSuccess: () => {
        setIsTitlePusherNotificationActive(false);
        setCalculationActivity((prev) => {
          if (!prev.isCalculating) return prev;
          return {
            ...prev,
            isCalculating: false,
            completedAt: new Date().toISOString(),
          };
        });
        onSuccess?.(calculationHref);
      },
      onError: () => {
        setTitleCalculationPusherErrorMessage(
          'Failed to cancel calculation. Please try again.'
        );
      },
    });
  }, [
    cancelCalculationState.loading,
    calculationHref,
    cancelHref,
    cancelCalculation,
    setIsTitlePusherNotificationActive,
    setCalculationActivity,
    onSuccess,
    setTitleCalculationPusherErrorMessage,
  ]);

  const canCancelCalculation =
    window.CONFIG?.featureFlags?.newTitleCalculatorEnabled;

  if (isTitlePusherNotificationActive) {
    return (
      <div className={style.buttonContainer}>
        <Button size="md" renderIcon={Calculator} disabled>
          <Loading description="Calculating..." small />
          Calculate
        </Button>
        {canCancelCalculation && (
          <DefinitionTooltip
            className={style.toolTip}
            align="top"
            definition="Cancel running calculation"
          >
            <Button
              kind="danger"
              size="md"
              onClick={handleCancelCalculation}
              disabled={cancelCalculationState.loading}
              renderIcon={(props) => <Close size={20} {...props} />}
            >
              Cancel
            </Button>
          </DefinitionTooltip>
        )}
      </div>
    );
  }

  return (
    <DefinitionTooltip
      className={style.toolTip}
      align="top"
      definition={
        !startingInterestDate ? (
          <span className={style.toolTipText}>
            {'Please add a\nstarting interest\nthen calculate'}
          </span>
        ) : (
          'Calculate workspace'
        )
      }
    >
      <Button
        size="md"
        onClick={() => {
          onTitleCalculationError(false);
          runCalculation({
            url: calculationsHref,
            type: 'POST',
            data: {
              asOfDate: getCurrentDateString(),
            },
            onSuccess: (data) => {
              setIsTitlePusherNotificationActive(true);
              setNewCalculation(data);
              setCalculationActivity({
                isCalculating: true,
                startedAt: new Date().toISOString(),
                completedAt: null,
                progress: 0,
              });
            },
            onError: () => {
              setIsTitlePusherNotificationActive(false);
              onTitleCalculationError(true);
            },
          });
        }}
        renderIcon={Calculator}
        disabled={calculation.loading || !startingInterestDate}
      >
        Calculate
      </Button>
    </DefinitionTooltip>
  );
};

export { RecalculateWorkspace };
